/**
 * Tooltip Component - Beautiful reusable tooltip for hover interactions
 */

import { ReactNode, useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ds } from '../../../design-system/tokens';

interface TooltipProps {
  /** Content to show in tooltip - can be string or ReactNode */
  text?: string;
  content?: ReactNode;
  /** Child element that triggers the tooltip */
  children: ReactNode;
  /** Position of tooltip relative to trigger element */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Additional delay before showing tooltip (ms) */
  delay?: number;
  /** Variant style */
  variant?: 'default' | 'glass' | 'pro';
  /** Size */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the tooltip wrapper should be full width */
  fullWidth?: boolean;
  /** When true, immediately hides the tooltip and suppresses future shows */
  disabled?: boolean;
}

const sizeClasses = {
  sm: 'px-3 py-1.5 min-h-[28px] max-w-[240px]',
  md: 'px-3.5 py-2 min-h-[30px] max-w-[280px]',
  lg: 'px-3.5 py-2 min-h-[32px] max-w-[320px]',
} as const;

const variantClasses = {
  default: 'bg-gray-900 text-white shadow-lg',
  glass: `bg-white ${ds.color.text.primary} shadow-[0_4px_12px_rgba(0,0,0,0.08)]`,
  pro: 'bg-emerald-500 text-white',
} as const;

// macOS-style structured text: first line = title (13px medium), rest = body (12px regular, secondary color)
function renderText(raw: string) {
  const lines = raw.split('\n');
  const title = lines[0];
  const body = lines.slice(1).filter(l => l.length > 0);

  if (body.length === 0) {
    return <span className="text-[13px] font-medium leading-[1.4]">{title}</span>;
  }

  return (
    <>
      <div className="text-[13px] font-medium leading-[1.4]">{title}</div>
      <div className={`text-[12px] font-normal ${ds.color.text.caption} leading-[1.4] mt-0.5`}>
        {body.map((line, i) => <div key={i}>{line}</div>)}
      </div>
    </>
  );
}

export function Tooltip({
  text,
  content,
  children,
  position = 'top',
  delay = 300,
  variant = 'glass',
  size = 'sm',
  fullWidth = false,
  disabled = false
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; transform: string }>({ top: 0, left: 0, transform: 'none' });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Immediately dismiss when disabled
  useEffect(() => {
    if (disabled) {
      clearTimeout(timerRef.current);
      setIsVisible(false);
      setShouldRender(false);
    }
  }, [disabled]);

  const computePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 8;

    switch (position) {
      case 'bottom':
        setCoords({ top: rect.bottom + gap, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' });
        break;
      case 'top':
        setCoords({ top: rect.top - gap, left: rect.left + rect.width / 2, transform: 'translateX(-50%) translateY(-100%)' });
        break;
      case 'left':
        setCoords({ top: rect.top + rect.height / 2, left: rect.left - gap, transform: 'translateX(-100%) translateY(-50%)' });
        break;
      case 'right':
        setCoords({ top: rect.top + rect.height / 2, left: rect.right + gap, transform: 'translateY(-50%)' });
        break;
    }
  }, [position]);

  const show = useCallback(() => {
    if (disabled) return;
    timerRef.current = setTimeout(() => {
      computePosition();
      setShouldRender(true);
      setTimeout(() => setIsVisible(true), 20);
    }, delay);
  }, [delay, disabled, computePosition]);

  const hide = useCallback(() => {
    clearTimeout(timerRef.current);
    setIsVisible(false);
    setTimeout(() => setShouldRender(false), 200);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Recompute position on scroll/resize while visible
  useEffect(() => {
    if (!shouldRender) return;
    const handler = () => computePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [shouldRender, computePosition]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        className={fullWidth ? 'flex w-full' : 'inline-flex'}
      >
        {children}
      </div>

      {/* Tooltip Portal */}
      {shouldRender && createPortal(
        <div
          className={`
            fixed z-[10400]
            ${sizeClasses[size]}
            ${variantClasses[variant]}
            rounded-[10px]
            text-left
            pointer-events-none select-none
            transition-opacity duration-150 ease-out
            ${isVisible ? 'opacity-100' : 'opacity-0'}
          `}
          style={{
            top: coords.top,
            left: coords.left,
            transform: coords.transform,
          }}
          role="tooltip"
        >
          {content || (text ? renderText(text) : null)}
        </div>,
        document.body
      )}
    </>
  );
}
